import { useState, useEffect } from "react";
import CryptoJS from "crypto-js";
import { environment as envConfig } from "../../utils/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faSearch, 
    faSun, 
    faMoon, 
    faGlobe, 
    faKey, 
    faTimes, 
    faExclamationTriangle, 
    faChartLine, 
    faList, 
    faCode, 
    faFileAlt, 
    faFingerprint, 
    faInfoCircle, 
    faCalendarCheck, 
    faCalendarPlus, 
    faHashtag, 
    faEye, 
    faCheckCircle, 
    faClock, 
    faExternalLinkAlt, 
    faCopy
} from "@fortawesome/free-solid-svg-icons";

export default function Explorer() {
    const [auditId, setAuditId] = useState("");
    const [selectedEnv, setSelectedEnv] = useState("production");
    const [auditData, setAuditData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("log");
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('explorer-theme');
        return savedTheme || 'light';
    });

    const environments = [
        { value: "production", label: "Production", baseUrl: "https://app.rocketfuel.inc" },
        { value: "sandbox", label: "Sandbox", baseUrl: "https://app-sandbox.rocketfuel.inc" },
        { value: "preprod", label: "Stage 1", baseUrl: "https://preprod-app.rocketdemo.net" },
    ];

    const getEnvironmentConfig = (env) => {
        return envConfig[env] || envConfig.sandbox;
    };

    const encryptPayload = (merchantId, clientSecret) => {
        const payload = {
            merchantId: merchantId,
            totop: ''
        };
        return CryptoJS.AES.encrypt(JSON.stringify(payload), clientSecret).toString();
    };

    const getAuthToken = async (env) => {
        const config = getEnvironmentConfig(env);
        const selectedEnvData = environments.find(e => e.value === env);
        
        if (!config.clientId || !config.clientSecret || !config.merchantId) {
            throw new Error(`Missing configuration for ${env} environment. Please check your environment variables.`);
        }

        const encryptedPayload = encryptPayload(config.merchantId, config.clientSecret);
        
        const response = await fetch(`${selectedEnvData.baseUrl}/api/auth/generate-auth-token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                clientId: config.clientId,
                encryptedPayload: encryptedPayload
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Authentication failed: ${response.status} ${response.statusText}. ${errorText}`);
        }

        const res = await response.json();
        const data = res.result;
        const token = data.access;
        
        if (!token) {
            throw new Error(`No access token found in response. Response keys: ${Object.keys(data).join(', ')}`);
        }
        
        return token;
    };

    const fetchAuditDetails = async (auditId, accessToken, env) => {
        const selectedEnvData = environments.find(e => e.value === env);
        
        const response = await fetch(`${selectedEnvData.baseUrl}/api/merchant/audit/${auditId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch audit details: ${response.status} ${response.statusText}`);
        }
        const res = await response.json();
        const data = res.result;
        return data;
    };

    const handleSearch = async () => {
        if (!auditId.trim()) {
            setError("Please enter an audit ID");
            return;
        }

        setLoading(true);
        setError("");
        setAuditData(null);

        try {
            // Step 1: Get authentication token
            const accessToken = await getAuthToken(selectedEnv);
            
            // Step 2: Fetch audit details
            const auditDetails = await fetchAuditDetails(auditId, accessToken, selectedEnv);
            
            setAuditData(auditDetails);
        } catch (err) {
            setError(err.message || "An error occurred while fetching audit details");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setAuditId("");
        setAuditData(null);
        setError("");
    };

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        console.log('Current theme:', theme, 'Switching to:', newTheme);
        setTheme(newTheme);
        localStorage.setItem('explorer-theme', newTheme);
    };

    useEffect(() => {
        console.log('Theme changed to:', theme);
    }, [theme]);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: 'UTC'
        }) + ' UTC';
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'success': 'success',
            'failed': 'danger',
            'pending': 'warning',
            'processing': 'info'
        };
        const color = statusColors[status?.toLowerCase()] || 'secondary';
        const getStatusIcon = (status) => {
            switch (status?.toLowerCase()) {
                case 'STATUS_VERIFIED'.toLowerCase(): return faCheckCircle;
                default: return faClock;
            }
        };
        return (
            <span className={`badge bg-${color} px-3 py-2`}>
                <FontAwesomeIcon icon={getStatusIcon(status)} className="me-1" />
                {status?.toUpperCase() || 'UNKNOWN'}
            </span>
        );
    };

    const getExplorerUrl = (hash, environment) => {
        if (!hash) return null;
        
        // Map environments to their respective networks
        const networkMap = {
            'qa': 'testnet',        // Stage 2 -> testnet
            'sandbox': 'testnet',   // Sandbox -> testnet
            'preprod': 'mainnet',   // Stage 1 -> mainnet
            'production': 'mainnet' // Production -> mainnet
        };
        
        const network = networkMap[environment] || 'testnet';
        return `https://ccdexplorer.io/${network}/transaction/${hash}`;
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            // You could add a toast notification here if needed
            console.log('URL copied to clipboard:', text);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    const renderLogView = () => {
        if (!auditData) return null;

        return (
            <div className="log-view">
                <div className="row g-1">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-gradient-primary text-white py-1">
                                <h6 className="mb-0 small">
                                    <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                                    Audit Log Details
                                </h6>
                            </div>
                            <div className="card-body p-1 p-md-2">
                                <div className="row g-1">
                                    <div className="col-12 col-md-6">
                                        <div className="info-item">
                                            <label 
                                                className="form-label fw-bold"
                                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                            >
                                                <FontAwesomeIcon icon={faFingerprint} className="me-1" />
                                                Log ID
                                            </label>
                                            <div className="info-value">
                                                <code 
                                                    className="px-2 py-1 rounded text-break"
                                                    style={{
                                                        backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                                                        color: theme === 'dark' ? '#e2e8f0' : '#333',
                                                        border: theme === 'dark' ? '1px solid #444' : '1px solid #e9ecef'
                                                    }}
                                                >
                                                    {auditData.id || 'N/A'}
                                                </code>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="info-item">
                                            <label 
                                                className="form-label fw-bold"
                                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                            >
                                                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                                Status
                                            </label>
                                            <div className="info-value">
                                                {getStatusBadge(auditData.status)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="info-item">
                                            <label 
                                                className="form-label fw-bold"
                                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                            >
                                                <FontAwesomeIcon icon={faCalendarCheck} className="me-1" />
                                                Verified On
                                            </label>
                                            <div className="info-value">
                                                <FontAwesomeIcon 
                                                    icon={faClock}
                                                    className="me-1"
                                                    style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                                />
                                                {formatDate(auditData.verifiedOn)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="info-item">
                                            <label 
                                                className="form-label fw-bold"
                                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                            >
                                                <FontAwesomeIcon icon={faCalendarPlus} className="me-1" />
                                                Created At
                                            </label>
                                            <div className="info-value">
                                                <FontAwesomeIcon 
                                                    icon={faClock}
                                                    className="me-1"
                                                    style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                                />
                                                {formatDate(auditData.createdAt)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="info-item">
                                            <label 
                                                className="form-label fw-bold"
                                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                            >
                                                <FontAwesomeIcon icon={faHashtag} className="me-1" />
                                                Hash
                                            </label>
                                            <div className="info-value">
                                                <code 
                                                    className="px-2 py-1 rounded d-block text-break"
                                                    style={{
                                                        backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                                                        color: theme === 'dark' ? '#e2e8f0' : '#333',
                                                        border: theme === 'dark' ? '1px solid #444' : '1px solid #e9ecef'
                                                    }}
                                                >
                                                    {auditData.hash || 'N/A'}
                                                </code>
                                                {auditData.hash && (
                                                    <div className="mt-2">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <label 
                                                                className="form-label fw-bold mb-0 small"
                                                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                                            >
                                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                                                Explorer URL
                                                            </label>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={getExplorerUrl(auditData.hash, selectedEnv)}
                                                                readOnly
                                                                style={{
                                                                    backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
                                                                    color: theme === 'dark' ? '#e2e8f0' : '#333',
                                                                    border: theme === 'dark' ? '1px solid #444' : '1px solid #e9ecef',
                                                                    fontSize: '0.8rem'
                                                                }}
                                                            />
                                                            <button
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => copyToClipboard(getExplorerUrl(auditData.hash, selectedEnv))}
                                                                title="Copy URL to clipboard"
                                                                style={{
                                                                    borderColor: theme === 'dark' ? '#4a5568' : '#007bff',
                                                                    color: theme === 'dark' ? '#e2e8f0' : '#007bff',
                                                                    fontSize: '0.8rem',
                                                                    padding: '0.25rem 0.5rem'
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faCopy} />
                                                            </button>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() => window.open(getExplorerUrl(auditData.hash, selectedEnv), '_blank')}
                                                                title="Open in new tab"
                                                                style={{
                                                                    backgroundColor: theme === 'dark' ? '#4a5568' : '#007bff',
                                                                    borderColor: theme === 'dark' ? '#4a5568' : '#007bff',
                                                                    fontSize: '0.8rem',
                                                                    padding: '0.25rem 0.5rem'
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="info-item">
                                            <label 
                                                className="form-label fw-bold"
                                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                            >
                                                <FontAwesomeIcon icon={faGlobe} className="me-1" />
                                                Timezone
                                            </label>
                                            <div className="info-value">
                                                <span className="badge bg-info">
                                                    <FontAwesomeIcon icon={faClock} className="me-1" />
                                                    {auditData.timezone || 'UTC'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="info-item">
                                            <label 
                                                className="form-label fw-bold"
                                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6c757d' }}
                                            >
                                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                                Presentation Context
                                            </label>
                                            <div className="info-value">
                                                <div 
                                                    className="p-2 rounded"
                                                    style={{
                                                        backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                                                        border: theme === 'dark' ? '1px solid #444' : '1px solid #e9ecef'
                                                    }}
                                                >
                                                    <pre 
                                                        className="mb-0" 
                                                        style={{ 
                                                            fontSize: '0.8rem',
                                                            color: theme === 'dark' ? '#e2e8f0' : '#6c757d',
                                                            fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                                                        }}
                                                    >
                                                        {auditData.presentationContext ? 
                                                            JSON.stringify(auditData.presentationContext, null, 2) : 
                                                            'No presentation context available'
                                                        }
                                                    </pre>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderRawView = () => {
        if (!auditData) return null;

        return (
            <div className="raw-view">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-gradient-dark text-white py-1">
                        <h6 className="mb-0 small">
                            <FontAwesomeIcon icon={faCode} className="me-1" />
                            Raw JSON Response
                        </h6>
                    </div>
                    <div className="card-body p-0">
                        <pre className="mb-0 p-2" style={{ 
                            backgroundColor: '#2d3748', 
                            color: '#e2e8f0',
                            fontSize: '0.85rem',
                            maxHeight: '400px',
                            overflow: 'auto',
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                            lineHeight: '1.4'
                        }}>
                            {JSON.stringify(auditData, null, 2)}
                        </pre>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <style key={theme}>
                {`
                    .explorer-page {
                        background: ${theme === 'dark' 
                            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' 
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        };
                        min-height: 100vh;
                        padding: 2rem 0;
                        position: relative;
                        overflow-x: hidden;
                        transition: background 0.3s ease;
                    }
                    
                    .explorer-page::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
                        opacity: 0.3;
                        animation: float 20s ease-in-out infinite;
                    }
                    
                    @keyframes float {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-20px) rotate(1deg); }
                    }
                    
                    .card {
                        transition: all 0.3s ease;
                        animation: slideInUp 0.6s ease-out;
                        background: ${theme === 'dark' 
                            ? 'rgba(30, 30, 30, 0.95)' 
                            : 'rgba(255, 255, 255, 0.95)'
                        } !important;
                        color: ${theme === 'dark' ? '#e2e8f0' : '#333'} !important;
                        border: ${theme === 'dark' ? '1px solid #444' : 'none'} !important;
                    }
                    
                    .card:hover {
                        transform: translateY(-5px);
                        box-shadow: ${theme === 'dark' 
                            ? '0 20px 40px rgba(0,0,0,0.3)' 
                            : '0 20px 40px rgba(0,0,0,0.1)'
                        } !important;
                    }
                    
                    @keyframes slideInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    .info-item {
                        margin-bottom: 0.5rem;
                        padding: 0.5rem;
                        background: ${theme === 'dark' 
                            ? 'rgba(45, 45, 45, 0.8)' 
                            : 'rgba(248, 249, 250, 0.8)'
                        };
                        border-radius: 6px;
                        border-left: 2px solid #007bff;
                        transition: all 0.3s ease;
                        color: ${theme === 'dark' ? '#e2e8f0' : '#333'};
                    }
                    
                    .info-item:hover {
                        background: ${theme === 'dark' 
                            ? 'rgba(45, 45, 45, 1)' 
                            : 'rgba(248, 249, 250, 1)'
                        };
                        transform: translateX(5px);
                        box-shadow: ${theme === 'dark' 
                            ? '0 4px 15px rgba(0,0,0,0.3)' 
                            : '0 4px 15px rgba(0,0,0,0.1)'
                        };
                    }
                    
                    .info-value {
                        font-size: 0.9rem;
                        margin-top: 0.25rem;
                    }
                    
                    .btn-group .btn {
                        transition: all 0.3s ease;
                        position: relative;
                        overflow: hidden;
                    }
                    
                    .btn-group .btn::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: -100%;
                        width: 100%;
                        height: 100%;
                        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                        transition: left 0.5s;
                    }
                    
                    .btn-group .btn:hover::before {
                        left: 100%;
                    }
                    
                    .badge {
                        font-size: 0.9rem;
                        padding: 0.5rem 1rem;
                        border-radius: 20px;
                        animation: pulse 2s infinite;
                    }
                    
                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }
                    
                        .form-control, .form-select {
                            background-color: ${theme === 'dark' ? '#2d2d2d' : '#fff'};
                            color: ${theme === 'dark' ? '#e2e8f0' : '#333'};
                            border-color: ${theme === 'dark' ? '#555' : '#ced4da'};
                        }
                        
                        .form-control::placeholder, .form-select::placeholder {
                            color: ${theme === 'dark' ? '#9ca3af' : '#6c757d'} !important;
                            opacity: 1;
                        }
                        
                        .form-control::-webkit-input-placeholder {
                            color: ${theme === 'dark' ? '#9ca3af' : '#6c757d'} !important;
                            opacity: 1;
                        }
                        
                        .form-control::-moz-placeholder {
                            color: ${theme === 'dark' ? '#9ca3af' : '#6c757d'} !important;
                            opacity: 1;
                        }
                        
                        .form-control:-ms-input-placeholder {
                            color: ${theme === 'dark' ? '#9ca3af' : '#6c757d'} !important;
                            opacity: 1;
                        }
                    
                    .form-control:focus, .form-select:focus {
                        border-color: #007bff;
                        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                        transform: scale(1.02);
                        background-color: ${theme === 'dark' ? '#2d2d2d' : '#fff'};
                        color: ${theme === 'dark' ? '#e2e8f0' : '#333'};
                    }
                    
                    .form-label {
                        color: ${theme === 'dark' ? '#e2e8f0' : '#333'} !important;
                    }
                    
                    code {
                        background-color: ${theme === 'dark' ? '#1a1a1a' : '#f8f9fa'} !important;
                        color: ${theme === 'dark' ? '#e2e8f0' : '#333'} !important;
                    }
                    
                    .bg-light {
                        background-color: ${theme === 'dark' ? '#2d2d2d' : '#f8f9fa'} !important;
                        color: ${theme === 'dark' ? '#e2e8f0' : '#333'} !important;
                    }
                    
                    .spinner-border {
                        animation: spin 1s linear infinite;
                    }
                    
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    .raw-view pre {
                        background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
                        box-shadow: inset 0 2px 10px rgba(0,0,0,0.3);
                    }
                    
                    .log-view .card-header {
                        background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
                    }
                    
                    .raw-view .card-header {
                        background: linear-gradient(135deg, #343a40 0%, #212529 100%) !important;
                    }
                    
                    /* Mobile optimizations */
                    @media (max-width: 768px) {
                        .explorer-page {
                            padding: 1rem 0;
                        }
                        
                        .display-5 {
                            font-size: 2rem;
                        }
                        
                        .btn-group {
                            width: 100%;
                        }
                        
                        .btn-group .btn {
                            flex: 1;
                            font-size: 0.875rem;
                        }
                        
                        .info-item {
                            margin-bottom: 1rem;
                            padding: 0.75rem;
                        }
                        
                        .info-value {
                            font-size: 0.9rem;
                        }
                        
                        .card-body {
                            padding: 1rem !important;
                        }
                        
                        .form-control, .form-select {
                            font-size: 0.9rem;
                        }
                        
                        .badge {
                            font-size: 0.75rem;
                            padding: 0.375rem 0.75rem;
                        }
                    }
                    
                    @media (max-width: 576px) {
                        .container-fluid {
                            padding-left: 0.5rem;
                            padding-right: 0.5rem;
                        }
                        
                        .btn {
                            font-size: 0.8rem;
                            padding: 0.5rem 0.75rem;
                        }
                        
                        .card {
                            border-radius: 15px !important;
                        }
                        
                        .info-item {
                            padding: 0.5rem;
                        }
                    }
                `}
            </style>
            <div className="explorer-page">
            <div className="container-fluid px-2 px-md-3">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-9 col-xl-7">
                        {/* Header */}
                        <div className="text-center mb-3 mb-md-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div></div>
                                <h1 className="h2 h1-md fw-bold text-white mb-0">
                                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                                    Rocketfuel Audit Explorer
                                </h1>
                                <div 
                                    className="theme-switch-container"
                                    onClick={() => {
                                        console.log('Theme switch clicked!');
                                        toggleTheme();
                                    }}
                                    style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '30px',
                                        backgroundColor: theme === 'dark' ? '#4a5568' : '#e2e8f0',
                                        borderRadius: '15px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        border: '2px solid rgba(255, 255, 255, 0.2)',
                                        backdropFilter: 'blur(10px)',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'scale(1.05)';
                                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'scale(1)';
                                        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                    }}
                                    title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
                                >
                                    <div
                                        className="theme-switch-thumb"
                                        style={{
                                            position: 'absolute',
                                            top: '2px',
                                            left: theme === 'dark' ? '32px' : '2px',
                                            width: '24px',
                                            height: '24px',
                                            backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
                                            borderRadius: '50%',
                                            transition: 'all 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                            border: '1px solid rgba(0, 0, 0, 0.1)'
                                        }}
                                    >
                                        <FontAwesomeIcon 
                                            icon={theme === 'light' ? faSun : faMoon}
                                            style={{ 
                                                fontSize: '10px',
                                                color: theme === 'dark' ? '#e2e8f0' : '#f6ad55'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <p className="text-white-50 small d-none d-md-block mb-0">
                                Search and analyze audit logs across different environments
                            </p>
                        </div>


                        {/* Search Card */}
                        <div className="card border-0 shadow mb-3" style={{
                            background: theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '15px',
                            border: theme === 'dark' ? '1px solid #444' : 'none'
                        }}>
                            <div className="card-body p-3">
                                <div className="row g-2 g-md-3 mb-3">
                                    <div className="col-12 col-md-6">
                                        <label htmlFor="environment" className="form-label fw-bold small">
                                            <FontAwesomeIcon 
                                                icon={faGlobe}
                                                className="me-1 me-md-2"
                                                style={{ color: theme === 'dark' ? '#60a5fa' : '#007bff' }}
                                            />
                                            Environment
                                        </label>
                                        <select
                                            id="environment"
                                            className="form-select form-select-sm border-2"
                                            value={selectedEnv}
                                            onChange={(e) => setSelectedEnv(e.target.value)}
                                            disabled={loading}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            {environments.map((env) => (
                                                <option key={env.value} value={env.value}>
                                                    {env.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label htmlFor="auditId" className="form-label fw-bold small">
                                            <FontAwesomeIcon 
                                                icon={faKey}
                                                className="me-1 me-md-2"
                                                style={{ color: theme === 'dark' ? '#60a5fa' : '#007bff' }}
                                            />
                                            Audit ID
                                        </label>
                                        <input
                                            type="text"
                                            id="auditId"
                                            className="form-control form-control-sm border-2"
                                            placeholder="Enter audit ID"
                                            value={auditId}
                                            onChange={(e) => setAuditId(e.target.value)}
                                            disabled={loading}
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </div>
                                </div>

                                <div className="d-flex flex-column flex-md-row gap-2 justify-content-center">
                                    <button
                                        className="btn btn-primary btn-lg px-3 px-md-4 py-2"
                                        onClick={handleSearch}
                                        disabled={loading || !auditId.trim()}
                                        style={{ 
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(0, 123, 255, 0.4)',
                                            transition: 'all 0.3s ease',
                                            minWidth: '140px',
                                            fontSize: '0.9rem',
                                            fontWeight: '700',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!e.target.disabled) {
                                                e.target.style.transform = 'translateY(-2px) scale(1.02)';
                                                e.target.style.boxShadow = '0 6px 20px rgba(0, 123, 255, 0.6)';
                                                e.target.style.background = 'linear-gradient(135deg, #0056b3 0%, #004085 100%)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'translateY(0) scale(1)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.4)';
                                            e.target.style.background = 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)';
                                        }}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                <span className="d-none d-md-inline">Searching...</span>
                                                <span className="d-md-none">Search...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSearch} className="me-2" />
                                                <span className="d-none d-md-inline">Search Audit Log</span>
                                                <span className="d-md-none">Search</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        className="btn btn-outline-danger btn-lg px-3 px-md-4 py-2"
                                        onClick={handleClear}
                                        disabled={loading}
                                        style={{ 
                                            borderRadius: '8px', 
                                            minWidth: '120px', 
                                            fontSize: '0.9rem',
                                            fontWeight: '700',
                                            borderWidth: '2px',
                                            borderColor: '#dc3545',
                                            color: '#dc3545',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!e.target.disabled) {
                                                e.target.style.transform = 'translateY(-2px) scale(1.02)';
                                                e.target.style.backgroundColor = '#dc3545';
                                                e.target.style.borderColor = '#dc3545';
                                                e.target.style.color = '#fff';
                                                e.target.style.boxShadow = '0 6px 20px rgba(220, 53, 69, 0.4)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'translateY(0) scale(1)';
                                            e.target.style.backgroundColor = 'transparent';
                                            e.target.style.borderColor = '#dc3545';
                                            e.target.style.color = '#dc3545';
                                            e.target.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.2)';
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Error Alert */}
                        {error && (
                            <div className="alert alert-danger border-0 shadow-lg mb-4" role="alert" style={{ borderRadius: '15px' }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                {error}
                            </div>
                        )}

                        {/* Results Section */}
                        {auditData && (
                            <div className="card border-0 shadow" style={{
                                background: theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '15px',
                                border: theme === 'dark' ? '1px solid #444' : 'none'
                            }}>
                                <div className="card-header border-0 bg-transparent py-2">
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
                                        <h4 className="mb-0 h5">
                                            <FontAwesomeIcon 
                                                icon={faChartLine}
                                                className="me-1"
                                                style={{ color: theme === 'dark' ? '#10b981' : '#28a745' }}
                                            />
                                            Audit Results
                                        </h4>
                                        <div className="btn-group w-25 w-md-auto" role="group">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${activeTab === 'log' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setActiveTab('log')}
                                                style={{ 
                                                    borderRadius: '6px 0 0 6px',
                                                    backgroundColor: activeTab === 'log' 
                                                        ? (theme === 'dark' ? '#4a5568' : '#007bff')
                                                        : 'transparent',
                                                    color: activeTab === 'log' 
                                                        ? '#fff' 
                                                        : (theme === 'dark' ? '#e2e8f0' : '#007bff'),
                                                    borderColor: theme === 'dark' ? '#4a5568' : '#007bff'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faList} className="me-1" />
                                                <span className="d-none d-sm-inline">Log View</span>
                                                <span className="d-sm-none">Log</span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${activeTab === 'raw' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setActiveTab('raw')}
                                                style={{ 
                                                    borderRadius: '0 6px 6px 0',
                                                    backgroundColor: activeTab === 'raw' 
                                                        ? (theme === 'dark' ? '#4a5568' : '#007bff')
                                                        : 'transparent',
                                                    color: activeTab === 'raw' 
                                                        ? '#fff' 
                                                        : (theme === 'dark' ? '#e2e8f0' : '#007bff'),
                                                    borderColor: theme === 'dark' ? '#4a5568' : '#007bff'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faCode} className="me-1" />
                                                <span className="d-none d-sm-inline">Raw JSON</span>
                                                <span className="d-sm-none">Raw</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body p-2 p-md-3">
                                    {activeTab === 'log' ? renderLogView() : renderRawView()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </div>
        </>
    );
}
